import { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, applyTheme } from './store/auth';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/Login';
import { ChangePasswordPage } from './pages/ChangePassword';
import { OverviewsPage } from './pages/Overviews';
import { CampaignsPage } from './pages/Campaigns';
import { OffersPage } from './pages/Offers';
import { PublishersPage } from './pages/Publishers';
import { UsersPage } from './pages/Users';
import { ConversionsPage } from './pages/Conversions';
import { TrackingLinksPage } from './pages/TrackingLinks';
import { ProfilePage } from './pages/Profile';
import { StatisticsPage } from './pages/Statistics';
import { ClicksPage } from './pages/Clicks';
import { PostbackLogsPage } from './pages/PostbackLogs';
import { AuditLogPage } from './pages/AuditLog';
import { ForwardingPage } from './pages/Forwarding';
import { TrackerDomainsPage } from './pages/TrackerDomains';
import { SettingsPage } from './pages/Settings';
import { MyEarningsPage } from './pages/MyEarnings';
import { ConversionAlertProvider } from './components/ConversionAlertProvider';
import { LightningStrikeOverlay } from './components/LightningStrikeOverlay';

function ProtectedRoutes() {
  const { user, token, fetchMe } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (token && !user) fetchMe();
  }, [token, user, fetchMe]);

  useEffect(() => {
    if (user) applyTheme(user.themePreference);
  }, [user]);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
    } else if (user?.mustChangePassword && loc.pathname !== '/change-password') {
      navigate('/change-password', { replace: true });
    }
  }, [token, user, loc.pathname, navigate]);

  if (!token) return null;
  if (user?.mustChangePassword) return null;
  if (!user) return <div className="min-h-screen flex items-center justify-center text-text-tertiary font-mono text-sm">Loading...</div>;

  const isAdmin = user.role === 'admin';
  const isSuperAdmin = isAdmin && user.isSuperAdmin === true;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Navigate to="/overviews" replace />} />
        {/* Pages visible to both admin and user */}
        <Route path="/overviews" element={<OverviewsPage />} />
        <Route path="/statistics" element={<StatisticsPage />} />
        <Route path="/conversions" element={<ConversionsPage />} />
        <Route path="/clicks" element={<ClicksPage />} />
        <Route path="/tracking-links" element={<TrackingLinksPage />} />
        <Route path="/earnings" element={<MyEarningsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        {/* Admin-only pages */}
        {isAdmin && (
          <>
            <Route path="/campaigns" element={<CampaignsPage />} />
            <Route path="/offers" element={<OffersPage />} />
            <Route path="/publishers" element={<PublishersPage />} />
            <Route path="/tracker-domains" element={<TrackerDomainsPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </>
        )}
        {/* Super-admin only — also gated server-side (requireSuperAdmin) */}
        {isSuperAdmin && (
          <>
            <Route path="/forwarding" element={<ForwardingPage />} />
            <Route path="/postback-logs" element={<PostbackLogsPage />} />
            <Route path="/audit-log" element={<AuditLogPage />} />
          </>
        )}
        <Route path="*" element={<Navigate to="/overviews" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route path="/*" element={<ProtectedRoutes />} />
      </Routes>
      {/* Global conversion alerts — polls /api/v1/conversions on every page,
          plays notification sound + flashes sub_id everywhere visible. */}
      <ConversionAlertProvider />
      {/* Full-screen lightning strike — fires at the moment a new conversion
          is detected, hits the on-screen Sub ID with a dramatic SVG bolt. */}
      <LightningStrikeOverlay />
    </>
  );
}
